import { Dialog, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { getTotalUnreadCount, getUnreadBadgeLabel } from "../../pages/Mail/mailUnread";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SIDEBAR_CONTAINER =
  "border-r border-white/10 bg-[#121b39] shadow-[1px_0_0_rgba(255,255,255,0.04)]";
const SIDEBAR_CONTAINER_DARK =
  "border-r border-white/10 bg-[#060914] shadow-[1px_0_0_rgba(255,255,255,0.035)]";
const SIDEBAR_ACTIVE =
  "translate-x-1 bg-white/[0.105] text-white shadow-[inset_3px_0_0_#53d6c7,0_10px_24px_rgba(0,0,0,0.14)]";
const SIDEBAR_ACTIVE_DARK =
  "translate-x-1 bg-[#11192c] text-white shadow-[inset_3px_0_0_#53d6c7,0_10px_24px_rgba(0,0,0,0.2)]";
const SIDEBAR_INACTIVE =
  "text-[#aeb9dc] hover:bg-white/[0.055] hover:text-white hover:translate-x-0.5";
const SIDEBAR_INACTIVE_DARK =
  "text-[#8f9bbd] hover:bg-white/[0.055] hover:text-white hover:translate-x-0.5";
const SIDEBAR_CONTAINER_ADMIN_LIGHT =
  "border-r border-white/15 bg-[#2d4f8d] shadow-[1px_0_0_rgba(255,255,255,0.06)]";
const SIDEBAR_ACTIVE_ADMIN_LIGHT =
  "translate-x-1 bg-white/[0.18] text-white shadow-[inset_3px_0_0_#8ee8ff,0_10px_24px_rgba(20,39,78,0.18)]";
const SIDEBAR_INACTIVE_ADMIN_LIGHT =
  "text-[#e1ebff] hover:bg-white/[0.11] hover:text-white hover:translate-x-0.5";
const THEME_STORAGE_KEY = "rp-theme";
const SmallSideBar = ({
  Fragment,
  navigation,
  navigationSections,
  user,
  location,
  sidebarOpen,
  setSidebarOpen,
}) => {

  const [showDetails, setshowDetails] = useState(false);
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "auto"
  );
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  const { mailRooms, mailUnreadCount } = useSelector((state) => state.mail);
  const isAdmin = Number(user?.role) === 1;
  const isAdminLight = isAdmin && !isDark;
  const sidebarContainerClass = isDark
    ? SIDEBAR_CONTAINER_DARK
    : isAdminLight
    ? SIDEBAR_CONTAINER_ADMIN_LIGHT
    : SIDEBAR_CONTAINER;
  const sidebarActiveClass = isDark
    ? SIDEBAR_ACTIVE_DARK
    : isAdminLight
    ? SIDEBAR_ACTIVE_ADMIN_LIGHT
    : SIDEBAR_ACTIVE;
  const sidebarInactiveClass = isDark
    ? SIDEBAR_INACTIVE_DARK
    : isAdminLight
    ? SIDEBAR_INACTIVE_ADMIN_LIGHT
    : SIDEBAR_INACTIVE;

  const getAutoDarkState = () => {
    const hour = new Date().getHours();
    return !(hour >= 6 && hour < 19);
  };

  const applyTheme = (mode) => {
    const shouldDark =
      mode === "dark" ? true : mode === "light" ? false : getAutoDarkState();
    document.documentElement.classList.toggle("dark", shouldDark);
    document.documentElement.style.colorScheme = shouldDark ? "dark" : "light";
    setIsDark(shouldDark);
  };

  const onThemeChange = (mode) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeMode(mode);
    applyTheme(mode);
  };

  useEffect(() => {
    applyTheme(themeMode);
    if (themeMode !== "auto") return undefined;

    const timer = setInterval(() => {
      applyTheme("auto");
    }, 60000);
    return () => clearInterval(timer);
  }, [themeMode]);

  useEffect(() => {
    const syncThemeMode = () =>
      setThemeMode(localStorage.getItem(THEME_STORAGE_KEY) || "auto");
    window.addEventListener("storage", syncThemeMode);
    return () => window.removeEventListener("storage", syncThemeMode);
  }, []);
  const image = useMemo(() => {
    return "https://www.repeathealth.com/wp-content/uploads/2024/02/Tag-Repeat-RGB-wit.png";
  }, []);


  const unreadMailCount = useMemo(
    () => {
      if (mailUnreadCount == null) return getTotalUnreadCount(mailRooms);
      const count = Number(mailUnreadCount);
      return Number.isFinite(count) && count > 0 ? count : 0;
    },
    [mailRooms, mailUnreadCount]
  );

  const navSections = useMemo(() => {
    if (typeof navigationSections === "function") {
      return navigationSections(user, unreadMailCount).filter(
        (section) => section?.items?.length
      );
    }

    return [
      {
        id: "main",
        items:
          typeof navigation === "function"
            ? navigation(user, unreadMailCount)
            : [],
      },
    ];
  }, [navigation, navigationSections, unreadMailCount, user]);

  const mainSections = navSections.filter((section) => section.id !== "service");
  const serviceSections = navSections.filter((section) => section.id === "service");

  const ThemeIcon =
    themeMode === "light"
      ? SunIcon
      : themeMode === "dark"
      ? MoonIcon
      : ComputerDesktopIcon;

  const renderNavItem = (item, { nested = false } = {}) => (
    <li key={item.name} className={item.spacedBefore ? "mt-4" : undefined}>
      <NavLink
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          classNames(
            isActive || location.pathname.startsWith(item.href)
              ? sidebarActiveClass
              : sidebarInactiveClass,
            nested
              ? "group flex min-h-10 transform-gpu items-center gap-x-2.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium leading-5 transition-[background-color,color,box-shadow,transform] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70"
              : "group flex min-h-10 transform-gpu items-center gap-x-2.5 rounded-xl px-2.5 py-1.5 text-[12px] font-medium leading-5 transition-[background-color,color,box-shadow,transform] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70"
          )
        }
      >
        {item.href === "/mailbox" ? (
          <div className="relative flex flex-1 gap-x-2.5">
            {item?.count > 0 ? (
              <span
                className={classNames(
                  "absolute -left-3 -top-2 z-10 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[11px] font-bold ring-2",
                  isAdminLight
                    ? "bg-white text-[#262f5e] ring-[#262f5e]"
                    : "bg-[#00a884] text-[#111b21] ring-[#111b21]"
                )}
                aria-label={`${item.count} ongelezen berichten`}
              >
                {getUnreadBadgeLabel(item.count)}
              </span>
            ) : null}

            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="relative">{item.name}</span>
          </div>
        ) : (
          <div className="flex flex-1 gap-x-2.5">
            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {item.name}
          </div>
        )}
        {item?.children && (
          <button
            type="button"
            title={showDetails ? "Sluit de details" : "Open voor details"}
            className="rounded-full p-1 text-current/75 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setshowDetails((value) => !value);
            }}
          >
            {showDetails ? (
              <ChevronDownIcon className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 shrink-0" />
            )}
          </button>
        )}
      </NavLink>
      {item?.children && showDetails && (
        <ul className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-2">
          {item.children.map((child) => renderNavItem(child, { nested: true }))}
        </ul>
      )}
    </li>
  );

  const renderSection = (section, service = false) => (
    <div
      key={section.id}
      className={classNames(
        service ? "border-t border-white/10 pt-4" : ""
      )}
    >
      {section.label ? (
        <p
          className={classNames(
            "mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide",
            isAdminLight ? "text-[#91a0cf]" : "text-[#7f8db9]"
          )}
        >
          {section.label}
        </p>
      ) : null}
      <ul role="list" className="-mx-1.5 space-y-1">
        {section.items.map((item) => renderNavItem(item))}
      </ul>
    </div>
  );

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[70] lg:hidden"
        onClose={() => setSidebarOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-16 flex h-full w-full max-w-[19rem] flex-1">
              <div
                className={classNames(
                  "flex min-h-0 min-w-0 grow flex-col gap-y-5 overflow-y-auto px-5 pb-3 ring-1 ring-black/20",
                  sidebarContainerClass
                )}
              >
            <div className="flex h-16 shrink-0 items-center justify-center">
              <img
                className="h-9 w-auto"
                src={image}
                alt="Repeathealth"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <div className="space-y-3">
                {mainSections.map((section) => renderSection(section))}
              </div>
              <div className="mt-auto space-y-3 pt-5">
                {serviceSections.map((section) => renderSection(section, true))}
              </div>
              <div
                className={classNames(
                  "mt-3 rounded-xl border border-white/10 bg-white/[0.045] px-2.5 py-2",
                  isAdminLight ? "text-[#d9e0f7]" : "text-[#b8c3e3]"
                )}
              >
                <label className="flex items-center gap-2">
                  <ThemeIcon
                    className={classNames(
                      "h-4 w-4 shrink-0",
                      isAdminLight ? "text-[#aeb9df]" : "text-[#92a0c8]"
                    )}
                    aria-hidden="true"
                  />
                  <span
                    className={classNames(
                      "text-[11px] font-semibold uppercase tracking-wide",
                      isAdminLight ? "text-[#c8d1ee]" : "text-[#b8c3e3]"
                    )}
                  >
                    Thema
                  </span>
                  <select
                    value={themeMode}
                    onChange={(event) => onThemeChange(event.target.value)}
                    aria-label="Selecteer thema"
                    onClick={(event) => event.stopPropagation()}
                    className={classNames(
                      "ml-auto h-8 rounded-lg border border-white/15 bg-white/[0.055] px-2 text-[11px] font-semibold uppercase text-white outline-none transition-colors focus:border-[#58d3c8] focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70"
                    )}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </label>
                {/* Keep a tiny quick reset for users who prefer explicit auto restore */}
                <button
                  type="button"
                  onClick={() => onThemeChange("auto")}
                  className={classNames(
                    "mt-2 w-full rounded-xl border border-white/[0.12] bg-white/[0.025] px-2 py-1.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70",
                    isAdminLight
                      ? "text-[#c8d1ee] hover:bg-white/[0.08] hover:text-white"
                      : "text-[#92a0c8] hover:bg-white/[0.08] hover:text-white"
                  )}
                >
                  Reset auto
                </button>
              </div>
            </nav>
              </div>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 rounded-full p-2.5 transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#58d3c8]/70"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon className="h-6 w-6 text-[#e9edef]" aria-hidden="true" />
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SmallSideBar;
